
import { useEffect, useState } from "react"
import "@fortawesome/fontawesome-free/css/all.css"
import TypewriterResponsePlain from "./TypeWriterResponsePlain"
import { useNavigate, useParams, useOutletContext } from "react-router-dom"
import axios from "axios"

// Add this function to load search state from session storage
const loadSearchState = () => {
  try {
    const savedState = sessionStorage.getItem("questionSearchState")
    return savedState ? JSON.parse(savedState) : null
  } catch (error) {
    console.error("Failed to load search state:", error)
    return null
  }
}

const QuestionHeader = ({ title, date, onAIAssistantClick }) => {
  return (
    <div className="border-b border-gray-100 pb-6 mb-8 flex justify-between items-center">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h1>
        <div className="flex items-center text-sm text-gray-500 space-x-6">
          <span className="flex items-center">{date}</span>
        </div>
      </div>
      <div className="flex-shrink-0">
        <button
          onClick={onAIAssistantClick}
          className="flex items-center px-4 py-2 text-sm text-white bg-gradient-to-r from-red-500 to-red-700 border border-red-600 rounded-lg hover:from-red-600 hover:to-red-800 transition-colors duration-200 ease-in-out shadow-md hover:shadow-lg active:shadow-inner"
        >
          <i className="fa-solid fa-robot mr-2 text-white" />
          AI Assistant
        </button>
      </div>
    </div>
  )
}

const QuestionDescription = ({ description, qImgSrcList }) => {
  return (
    <div className="mb-10">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Problem Description</h2>
      {qImgSrcList && qImgSrcList.length > 0 ? (
        <div className="space-y-4">
          {qImgSrcList.map((imageSrc, index) => (
            <img
              key={index}
              src={imageSrc || "/placeholder.svg"}
              alt={`Problem related image ${index + 1}`}
              className="w-full max-w-lg mx-auto"
            />
          ))}
        </div>
      ) : null}
      <div className="prose max-w-none text-gray-700 leading-relaxed">
        <TypewriterResponsePlain content={description || ""} />
      </div>
    </div>
  )
}

const Solution = ({ solution, onGoBack }) => {
  const handleDownloadPdf = () => {
    // Create a print-friendly version of the content
    const printContent = document.createElement("div")
    printContent.style.padding = "30px"
    printContent.style.maxWidth = "1000px"
    printContent.style.margin = "0 auto"
    printContent.style.fontFamily = "system-ui, -apple-system, sans-serif"
    printContent.style.backgroundColor = "#fff"
    printContent.style.color = "#333"

    // Add header with title
    const header = document.createElement("header")
    header.style.display = "flex"
    header.style.justifyContent = "space-between"
    header.style.alignItems = "center"
    header.style.marginBottom = "30px"
    header.style.paddingBottom = "15px"
    header.style.borderBottom = "2px solid #e5e7eb"

    const title = document.createElement("h1")
    title.style.fontSize = "24px"
    title.style.fontWeight = "bold"
    title.style.color = "#1e3a8a"
    title.style.margin = "0"
    title.textContent = document.querySelector("h1").textContent || "Question Solution"

    const dateInfo = document.createElement("p")
    dateInfo.style.fontSize = "14px"
    dateInfo.style.color = "#666"
    dateInfo.style.margin = "0"
    dateInfo.textContent = document.querySelector(".text-sm.text-gray-500 span").textContent || ""

    header.appendChild(title)
    header.appendChild(dateInfo)
    printContent.appendChild(header)

    // Add question description
    const questionSection = document.createElement("div")
    questionSection.style.marginBottom = "30px"

    const questionTitle = document.createElement("h2")
    questionTitle.style.fontSize = "18px"
    questionTitle.style.fontWeight = "bold"
    questionTitle.style.marginBottom = "15px"
    questionTitle.style.color = "#333"
    questionTitle.textContent = "Problem Description"
    questionSection.appendChild(questionTitle)

    const questionContent = document.createElement("div")
    questionContent.style.fontSize = "15px"
    questionContent.style.lineHeight = "1.6"
    questionContent.style.padding = "20px"
    questionContent.style.backgroundColor = "#f8f9fa"
    questionContent.style.borderRadius = "8px"
    questionContent.style.border = "1px solid #e2e8f0"
    questionContent.innerHTML = document.querySelector(".prose.max-w-none").innerHTML
    questionSection.appendChild(questionContent)

    printContent.appendChild(questionSection)

    // Add solution
    const solutionSection = document.createElement("div")
    solutionSection.style.marginTop = "30px"

    const solutionTitle = document.createElement("h2")
    solutionTitle.style.fontSize = "18px"
    solutionTitle.style.fontWeight = "bold"
    solutionTitle.style.marginBottom = "15px"
    solutionTitle.style.color = "#2563eb"
    solutionTitle.textContent = "Solution"
    solutionSection.appendChild(solutionTitle)

    const solutionContent = document.createElement("div")
    solutionContent.style.fontSize = "15px"
    solutionContent.style.lineHeight = "1.6"
    solutionContent.style.padding = "20px"
    solutionContent.style.backgroundColor = "#f0f9ff"
    solutionContent.style.borderRadius = "8px"
    solutionContent.style.border = "1px solid #bfdbfe"
    solutionContent.innerHTML = document.querySelector(".text-gray-700.leading-relaxed").innerHTML
    solutionSection.appendChild(solutionContent)

    printContent.appendChild(solutionSection)

    // Add footer
    const footer = document.createElement("footer")
    footer.style.marginTop = "40px"
    footer.style.paddingTop = "15px"
    footer.style.borderTop = "1px solid #e5e7eb"
    footer.style.textAlign = "center"
    footer.style.fontSize = "12px"
    footer.style.color = "#666"
    footer.textContent = "Generated by Question Bank System • " + new Date().toLocaleDateString()
    printContent.appendChild(footer)

    // Hide original content
    const navBar = document.getElementsByClassName("App bg-gray-50 max-h-screen flex flex-col")[0]
    if (navBar) {
      navBar.style.display = "none"
    }
    const backButton = document.getElementById("back-button")
    if (backButton) {
      backButton.style.display = "none"
    }
    const downloadButton = document.getElementById("download-button")
    if (downloadButton) {
      downloadButton.style.display = "none"
    }

    // Append to body and print
    document.body.appendChild(printContent)
    window.print()

    // Restore original content
    if (navBar) {
      navBar.style.display = "block"
    }
    if (backButton) {
      backButton.style.display = "block"
    }
    if (downloadButton) {
      downloadButton.style.display = "block"
    }
    document.body.removeChild(printContent)
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Solution</h2>
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 mb-6 prose max-w-none">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="text-gray-700 leading-relaxed">
            <TypewriterResponsePlain content={solution || ""} />
          </div>
          <div className="flex justify-between mt-8">
            <BackButton onGoBack={onGoBack} />
            <DownloadPdf handleDownloadPdf={handleDownloadPdf} />
          </div>
        </div>
      </div>
    </div>
  )
}

function BackButton({ onGoBack }) {
  const navigate = useNavigate()
  const {
    searchText,
    results,
    currentQuestions,
    selectedFilter,
    currentPage,
    itemsPerPage,
    selectedButton,
    selectedQuestions,
    includeSolution,
  } = useOutletContext() || {}

  const handleBack = () => {
    // Load the saved search state
    const savedState = loadSearchState()

    // Navigate back with state to preserve search results
    navigate(-1, {
      state: {
        searchText: savedState?.searchText || searchText,
        results: savedState?.results || results,
        currentQuestions: savedState?.currentQuestions || currentQuestions,
        selectedFilter: savedState?.selectedFilter || selectedFilter,
        currentPage: savedState?.currentPage || currentPage,
        itemsPerPage: savedState?.itemsPerPage || itemsPerPage,
        selectedButton: savedState?.selectedButton || selectedButton,
        selectedQuestions: savedState?.selectedQuestions || selectedQuestions,
        includeSolution: savedState?.includeSolution || includeSolution,
        returnedFromDetails: true, // Add this flag to indicate we're returning from details
      },
    })
  }

  return (
    <div id="back-button" className="flex justify-start print:none">
      <button
        onClick={handleBack}
        className="return-button flex items-center px-6 py-3 text-sm text-gray-600 bg-gray-100 border border-gray-400 rounded-lg hover:bg-gray-50 transition-colors duration-200 ease-in-out!rounded-button whitespace-nowrap shadow-sm"
      >
        <i className="fa-solid fa-arrow-left mr-2 text-gray-500" />
        Back to Question Search
      </button>
    </div>
  )
}

function DownloadPdf({ handleDownloadPdf }) {
  return (
    <button
      id="download-button"
      onClick={handleDownloadPdf}
      className="flex items-center px-6 py-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 ease-in-out !rounded-button whitespace-nowrap shadow-sm"
    >
      <i className="fa-solid fa-file-pdf mr-2 text-gray-500" />
      Download PDF
    </button>
  )
}

function QuestionDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [question, setQuestion] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`/api/exam-question/${id}`)
        setQuestion(response.data)
      } catch (error) {
        console.error("Failed to fetch question:", error)
        setError("Failed to fetch question. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchQuestion()
  }, [id])

  const handleAIAssistantClick = () => {
    const prompt = `Please provide a detailed explanation for the following question and solution:
    
    Question: ${encodeURIComponent(question.questionContent)}
    
    Solution: ${encodeURIComponent(question.solutionContent)}
    
    Please elaborate on the key concepts, steps, and reasoning involved.`

    navigate("/chatbox", { state: { questionPrompt: prompt } })
  }

  const handleGoBack = () => {
    // We'll use the BackButton component's logic instead
    navigate(-1)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-red-600 mb-4"></i>
          <p className="text-gray-600">Loading question details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <i className="fas fa-exclamation-circle text-4xl text-red-600 mb-4"></i>
          <p className="text-gray-700 font-medium">{error}</p>
          <button onClick={handleGoBack} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  if (!question) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <i className="fas fa-question-circle text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-700">Question not found.</p>
          <button onClick={handleGoBack} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Return to Search
          </button>
        </div>
      </div>
    )
  }

  const { questionImages, questionContent, solutionContent } = question

  // Process solution content to handle object marking schemes
  const processMarkingScheme = (markingScheme) => {
    if (markingScheme && typeof markingScheme === "object") {
      try {
        return JSON.stringify(markingScheme, null, 2)
      } catch (e) {
        return "See detailed marking scheme in solution"
      }
    }
    return markingScheme
  }

  const joinedSolution =
    solutionContent &&
    solutionContent.Parts &&
    solutionContent.Parts.map((part) => {
      const processedMarkingScheme = processMarkingScheme(part.MarkingScheme)
      return [part.Solution, processedMarkingScheme ? `\n Marking Scheme: ${processedMarkingScheme} \n` : ""]
        .filter(Boolean)
        .join("\n")
    }).join("\n")

  return (
    <div className="min-h-[1024px] w-[1440px] mx-auto bg-gray-50 text-left print:w-full print:text-left">
      <div className="max-w-4xl mx-auto px-8 py-12 print:w-full print:text-left">
        <div className="bg-white rounded-lg shadow-sm p-10">
          <QuestionHeader
            title={question.questionTitle}
            date={question.yearOfExam}
            onAIAssistantClick={handleAIAssistantClick}
          />
          <QuestionDescription description={questionContent} qImgSrcList={questionImages} />
          <Solution solution={joinedSolution} onGoBack={handleGoBack} />
        </div>
      </div>
    </div>
  )
}

export default QuestionDetail
